// src/components/desk/NotificationsPanel.jsx — THE notifications destination (design ruling
// 2026-07-24, X-model consolidation): the command-strip bell pill is the sole entry point,
// this right-side overlay is the sole destination. The feed rows moved here VERBATIM from
// AddEntryTile (which is pure quick-actions again) — four streams interleaved newest-first:
// pending/failed drafts (actionable — these badge), access-request decisions (badge until
// seen), 7-day price moves and ended listings (ambient — in the feed, never the badge).
// Desktop: ~360px slide-over overlaying the ranking rail; mobile: full-width. Dismissed by
// ✕ / Escape / outside-click. Every row deep-links into the main column via the same
// onOpen* callbacks the tile used.

import { useEffect, useMemo, useState } from 'react'
import { t, tLog, tOr, getLanguage } from '../../lib/i18n.js'
import { districtLabel } from '../../lib/districtLabel.js'
import { Icon } from '../Icon.jsx'
import { pill, timeAgo } from './primitives.jsx'
import { moneyText } from '../../lib/money.js'
import { kindOf, FIELD_LABEL_KEYS } from './HistoryCards.jsx'
import { getPriceChanges, decideAccessRequest, getReviewQueues, getSharePlanNotices } from '../../db/core.js'
import { MEETING_STATUS_KEYS, NUDGE_WHY_ICON, fmtDay } from './MeetingsFeed.jsx'
import { fmtTbilisi } from '../../lib/tbilisiTime.js'
// ONE statement of which rows are which class — its twin is required by the server to
// decide what may be dismissed. See that file's header for why it is not stated here.
import { ROW_CLASS, CONDITIONAL_PREFIXES, rowPrefix } from '../../../core/notification-class.mjs'

// Verb-first label so the feed reads as actions, not objects. human_edited (set by
// IntakeTab on manual/edited proposals) is what lets us honestly say "You" vs "AI".
// (Moved with the feed — IntakeTab no longer renders activity rows itself.)
function activityLabel(item) {
  const k = kindOf(item), p = item.proposed || {}
  const edited = !!p.human_edited
  switch (k) {
    case 'extract_requirement': return { icon: <Icon name="user-check" />, text: edited ? t('desk_activity_added_client') : t('desk_activity_ai_parsed_client'), obj: p.person?.display_name || p.person?.phone }
    case 'amend_requirement':   return { icon: <Icon name="user-check" />, text: edited ? t('desk_activity_you_edited_client') : t('desk_activity_ai_edited_client'), obj: (p.ops || []).map(o => t(FIELD_LABEL_KEYS[o.field]) || o.field).slice(0, 2).join(', ') || null }
    case 'close_requirement':   return { icon: <Icon name="user-check" />, text: t('desk_activity_closed_client'), obj: null }
    case 'reopen_requirement':  return { icon: <Icon name="user-check" />, text: t('desk_activity_reopened_client'), obj: null }
    case 'extract_listing': {
      const pr = p.input?.property || {}
      const text = item.status === 'pending' ? t('desk_activity_parsed_property') : item.status === 'dismissed' ? t('desk_activity_archived_property') : t('desk_activity_saved_property')
      return { icon: <Icon name="house" />, text, obj: pr.address || pr.cadastral_code || null, pending: item.status === 'pending' }
    }
    case 'schedule_meeting':    return { icon: <Icon name="calendar" />, text: t('desk_activity_logged_meeting'), obj: p.meeting?.clientName || p.meeting?.clientPhone || null }
    case 'add_candidate':       return { icon: <Icon name="briefcase" />, text: t('desk_activity_added_candidate'), obj: p.name || p.phone || null }
    default:                    return { icon: '•', text: t('desk_activity_fallback'), obj: item.intake?.raw_text?.slice(0, 30) || null }
  }
}

// ── §2b type scale (design round 1, 2026-08-06) ──────────────────────────────
// This panel predated PILL_SYSTEM_SPEC §2b and violated it: row values at 12px
// (`text-xs`) and the medium-nudge tail at 11px, which §2b removes outright.
// Nothing of the old type is preserved. Three roles, no others.
const TXT_VALUE = 'text-[13.5px] leading-[1.45]'
const TXT_META = 'text-[12px] text-muted-foreground'
const TXT_ZONE = 'text-[13px] font-medium tracking-[.02em] text-muted-foreground'

// ── The three zones are three kinds of DEBT, not three kinds of event ────────
// Designer's push-back, round 1 2026-08-06, adopted as the model — it is a TEST
// rather than a taxonomy, so the next row type is settled without a meeting:
//
//   A — a debt to a PERSON.      Only these may badge. Resolve-only, never dismissed.
//   B — a debt to your JUDGEMENT. Nobody can call it in, so it may be dismissed.
//   C — not a debt at all.        A count, never rows; can never be unread.
//
// ⚠️ THE ORDER OF THE ZONES IS THE BUDGET. Recency sorts only WITHIN a zone, so
// ambient cannot climb — there is nowhere to climb to. This is why the old
// `.slice(0, 48)` over one time-sorted list is gone rather than merely enlarged:
// a cap plus a shared sort is what let 220 price moves a week bury the one row
// that was a person asking to view a flat. Do not reintroduce a global sort.
//
// `worked` is the salience set — listing ids the viewer is actually working
// (shared, has a meeting, holds a cooperation). Measured 2026-08-06: only 74 of
// 2,234 price moves in 7 days land on one, which is what makes B small enough to
// be rows at all.
// ⚠️ THE CLASS IS NOT DECIDED HERE. It is read from `core/notification-class.mjs`, whose
// twin the server requires to decide whether a row may be dismissed. This function's only
// job is to map a feed row to its PREFIX; the prefix decides the class, and rowKey() below
// mints that same prefix. One rule, one place — B4 was two copies of it with nothing
// detecting drift, which is the disease B261 and B273 already fixed elsewhere.
function classify(row, worked) {
  const prefix = rowPrefix(row)
  if (!prefix) return 'C'
  if (CONDITIONAL_PREFIXES.has(prefix)) {
    // The condition the server cannot see: is the viewer actually working this flat?
    // Match on EITHER key — property_id is the one that matters (a meeting and a
    // cooperation are keyed on the property, not the listing) but it only arrives from a
    // deployed server, so the listing match keeps this working against an older API.
    const r = row.pc || row.ec || {}
    return (worked.has(r.property_id) || worked.has(r.listing_id)) ? 'B' : 'C'
  }
  return ROW_CLASS[prefix] || 'C'
}

// ── The key a row is remembered by (0447) ────────────────────────────────────
// Stable across refetches, and its PREFIX is what the server reads to decide
// whether the row may be dismissed at all — so a prefix here must match a key in
// `KIND_CLASS` in lib/core_notifications.js. Adding a row type means adding it in
// BOTH places; an unknown prefix is refused by the endpoint rather than guessed
// at, which is the failure mode we want (a new row type is never silently
// dismissible).
//
// ⚠️ The key must not contain a timestamp that MOVES. `nudge:` deliberately keys
// on the meeting and the reason, not on `updated_at`: a nudge whose meeting is
// touched must stay the same row, or dismissing it would be undone by any edit.
function rowKey(row) {
  const p = rowPrefix(row)
  if (!p) return null
  switch (row.kind) {
    case 'ask': case 'access': return `${p}:${row.a.id}`
    case 'notif': return `${p}:${row.item.id}`
    case 'ai_alert': return `${p}:ai:${row.al.finding_key}`
    case 'system_alert': return `${p}:sys:${row.sa.key}`
    // The event id is the change itself — a second rewrite of the same deposit is a
    // second row, which is the point of the alert.
    case 'binder_change': return `${p}:${row.c.event_id}`
    case 'nudge': return `${p}:${row.n.id}:${row.n.why}`
    // ⚠️ For an ENGAGED row `row.s.id` is the server's synthetic
    // `eng:<share_id>:<session_start_ms>` — it changes when a NEW reading session
    // starts, which is what makes a client who returns a week later a new row
    // rather than one already marked read (B2). Do not "tidy" it to the share id.
    case 'share': return `${p}:${row.s.id}`
    case 'price': return `${p}:${row.pc.listing_id}:${row.pc.observed_at}`
    case 'ended': return `${p}:${row.ec.listing_id}:${row.ec.observed_at}`
    case 'share_run': return `${p}:${row.r.id}`
    // the client's last message id: a NEW message from the same client after a reply is a new row
    case 'client_waiting': return `${p}:${row.w.last_in_id}`
    default: return null
  }
}

// ── A stream, stated rather than listed ──────────────────────────────────────
// `lifted` is the load-bearing part of the label (designer, round 1): naming how
// many of this stream were promoted into zone B is what makes the digest an
// honest summary instead of a hiding place. Opens IN PLACE showing five, because
// the common case is a two-second glance and a navigation for a glance costs a
// return trip to a panel that has since re-sorted.
//
// Five is a SAMPLE, not a queue — sorted by magnitude, never by recency, so the
// question it answers is "is anything moving?" and not "what happened last".
function StreamDigest({ icon, label, rows, lifted = 0, sort, children }) {
  const [open, setOpen] = useState(false)
  if (!rows.length) return null
  const sample = (sort ? [...rows].sort(sort) : rows).slice(0, 5)
  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
        <span className="shrink-0 text-muted-foreground"><Icon name={icon} size={14} /></span>
        <span className={`min-w-0 flex-1 text-left truncate ${TXT_VALUE}`}>
          {label}
          {lifted > 0 && (
            <span className={TXT_META}> · {tLog('desk_notif_stream_lifted', { n: lifted })}</span>
          )}
        </span>
        <span className="shrink-0 text-[12px] font-medium tabular-nums">{rows.length}</span>
        {/* No chevron: it opens in place, it is not a door. */}
        <span className="shrink-0 text-muted-foreground text-[12px]">{open ? '⌃' : '⌄'}</span>
      </button>
      {open && <div className="pl-8 pr-2 pb-1 space-y-0.5">{children(sample, rows.length)}</div>}
    </div>
  )
}

// The feed alone, no chrome — reused by BOTH containers (2026-07-24 second ruling):
// desktop renders it inline in the right rail under the bell (above/instead of Agent
// Ranking), mobile keeps the slide-over via NotificationsPanel below. Same one-renderer
// rule as CooperationChips: containers differ, rows never fork.
// ── Inbound access request (MEETINGS_MODEL.md §4.4) ──────────────────────────
// The prompt that was missing: until 2026-07-24 the bell only showed decisions on
// YOUR OWN requests (scope='mine'), so an agent whose colleague was waiting on
// them learned nothing unless they happened to open the match panel, where
// AccessRequestsNotice lives. This row is the same decision, surfaced where
// interrupts belong — and it badges.
//
// The copy states the business reason: "people approve requests much more readily
// when the business context is obvious" (owner). ⚠️ It shows the meeting TIME and
// never the address — see the projection note on the server's shaped row.
// A WhatsApp client waiting 30+ min on the agent's own line (core 4226, owner 2026-11-04 "yes build E"). Shows who,
// how long, the client's last words and - when the client brain drafted one - a suggested reply with a Copy button, plus
// a link that opens the chat in WhatsApp. ⚠️ Nothing here sends: the agent reads, edits and sends it themselves, and
// the row leaves the feed by itself once they have written back in that chat.
function ClientWaitingRow({ w }) {
  const [copied, setCopied] = useState(false)
  const mins = Number(w.waiting_min) || 0
  const waited = mins >= 120 ? tLog('notif_client_waiting_hours', { n: Math.floor(mins / 60) }) : tLog('notif_client_waiting_min', { n: mins })
  const copy = () => {
    try { navigator.clipboard.writeText(String(w.draft || '')).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }) } catch { /* no clipboard: the text stays on screen */ }
  }
  return (
    <div className="w-full flex items-start gap-2 px-2 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/30">
      <span className="shrink-0 text-sm leading-5"><Icon name="message-circle" /></span>
      <span className="min-w-0 flex-1 text-xs">
        <span className="font-medium">{t('notif_client_waiting')}</span>
        {' · '}{w.title || (w.phone9 ? `+995 ${w.phone9}` : '')}
        <span className="block text-amber-700 dark:text-amber-400">{waited}</span>
        {w.in_text && <span className="block text-muted-foreground whitespace-pre-line line-clamp-3">„{w.in_text}"</span>}
        {w.draft && (
          <span className="block mt-1 p-1.5 rounded bg-background border border-border">
            <span className="block text-[11px] text-muted-foreground">{t('notif_client_waiting_suggested')}</span>
            <span className="block whitespace-pre-line break-words">{w.draft}</span>
            <button type="button" onClick={copy} className="mt-1 text-emerald-600 underline">{copied ? t('notif_client_waiting_copied') : t('notif_client_waiting_copy')}</button>
          </span>
        )}
        {w.phone9 && (
          <a href={`https://wa.me/995${w.phone9}`} target="_blank" rel="noreferrer" className="inline-block mt-1 text-emerald-600 underline">{t('notif_client_waiting_open')}</a>
        )}
      </span>
    </div>
  )
}

function AccessAskRow({ r, onDecided }) {
  const [busy, setBusy] = useState(false)
  const decide = async (action, scope) => {
    if (busy) return
    setBusy(true)
    try { await decideAccessRequest(r.id, action, null, scope); onDecided?.(r.id) }
    catch { setBusy(false) }   // stay actionable on failure — a silently vanished request is worse
  }
  // TBILISI (owner ruling 2026-08-12). This row's header says the meeting TIME is the whole
  // business context offered for the access decision — so a device-zone hour is not a
  // formatting slip here, it is the fact the decision rests on.
  const when = r.meeting?.scheduled_at
    ? fmtTbilisi(r.meeting.scheduled_at,
        { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  return (
    <div className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
      <span className="shrink-0 text-sm leading-5"><Icon name="bell" /></span>
      <span className="min-w-0 flex-1">
        <span className={TXT_VALUE}>
          {tLog(r.requirement_id ? 'desk_notif_access_ask_client' : 'desk_notif_access_ask_owner',
            { name: r.requester?.name || '?' })}
        </span>
        {r.meeting_id && (
          <span className={`block ${TXT_META}`}>
            {when ? tLog('desk_notif_access_meeting_at', { when }) : t('desk_notif_access_meeting_untimed')}
          </span>
        )}
        {r.context && !r.meeting_id && (
          <span className={`block ${TXT_META}`}><Icon name="message-circle" size={11} /> {r.context}</span>
        )}
        <span className="flex flex-wrap items-center gap-1.5 mt-1">
          {/* Wording is INTENT, never enforcement (0195) — do not relabel these
              "temporary"/"permanent". */}
          <button disabled={busy} onClick={() => decide('grant', 'this_meeting')}
            className={pill('ok', { door: true, extra: 'disabled:opacity-40' })}>
            <Icon name="check" size={11} /> {t('desk_access_grant_meeting_btn')}
          </button>
          <button disabled={busy} onClick={() => decide('grant', 'until_revoked')}
            className={pill('neutral', { door: true, extra: 'hover:bg-accent disabled:opacity-40' })}>
            {t('desk_access_grant_ongoing_btn')}
          </button>
          <button disabled={busy} onClick={() => decide('reject')}
            className={pill('none', { door: true, extra: 'border-destructive/50 text-destructive hover:bg-destructive/10 disabled:opacity-40' })}>
            <Icon name="x" size={11} /> {t('desk_access_deny_btn')}
          </button>
        </span>
        <span className={`block ${TXT_META} mt-0.5`}>{timeAgo(r.created_at)}</span>
      </span>
    </div>
  )
}

// A client did something with a link you sent. `meeting_requested` is the one
// that matters — it is a person asking to see the flat, and the share page has
// already told them "your agent has been notified", so this row is what makes
// that true. Opens are ambient context, never a demand for action.
// District is a PATH, not a code (2026-07-26 owner report: this row rendered
// "· TBS_VEDZISI"). The server resolves ancestry — see core_share.js districtPath —
// and sends it outermost-first, so a sub-district reads "საბურთალო, ვეძისი" rather
// than a bare "ვეძისი" that hides its district. No district_code fallback anywhere,
// deliberately: an internal identifier is worse than no location at all.
// Used by EVERY row in this feed that names a district, not just share rows
// (renamed 2026-08-02): the access-decision row rendered the raw `TBS_SABURTALO`
// because it reached for district_code while this helper sat two branches away.
// Both server payloads already carry district_path — one renderer, no second rule.
const rowDistrictLabel = (r) => districtLabel(r?.district_path, getLanguage())

// ── The subject of a share row, in the case the sentence needs ────────────────
// Owner-visible bug, fixed 2026-08-06: the Georgian templates hardcoded the
// ergative suffix (`{name}-მა გახსნა…`) while the anonymous fallback was ALREADY
// ergative — so every one of the 302-of-306 shares with no recorded recipient
// rendered „კლიენტმა-მა". The suffix now lives with the subject, not the sentence.
//
// The rule is the ordinary Georgian ergative for proper nouns: a vowel-final name
// takes -მ (ნინო → ნინომ), a consonant-final one -მა. It is an approximation, not
// a morphology engine, and it deliberately leaves non-Georgian scripts untouched
// — a Latin name gets no suffix rather than a wrong one. The overwhelmingly
// common path is the anonymous fallback, which is simply correct.
const KA_VOWELS = 'აეიოუ'
function shareSubject(name) {
  if (!name) return t('desk_notif_share_someone')
  if (getLanguage() !== 'ka') return name
  const last = name.trim().slice(-1)
  if (!/[Ⴀ-ჿ]/.test(last)) return name
  return name + (KA_VOWELS.includes(last) ? 'მ' : 'მა')
}

// One side of a deposit change. ⚠️ The CURRENCY is always printed, never dropped when the
// two sides share one: 5,000 GEL → 5,000 USD is the change the digits alone would hide, and
// it is exactly the edit worth noticing. A missing amount prints as an em dash rather than
// "0" — a cleared deposit and a zero deposit are different claims.
const fmtBinder = (s) => {
  if (!s || s.amount == null) return '—'
  const n = Number(s.amount)
  return `${Number.isFinite(n) ? n.toLocaleString('en-US') : s.amount}${s.currency ? ` ${s.currency}` : ''}`
}
// WHICH part of the deposit moved. The server alerts on amount, currency OR expiry, so the
// row has to be able to say which — see the render note on the binder_change branch.
// 'amount' covers a currency swap too: 5,000 GEL → 5,000 USD is a change of what is owed.
const binderMoved = (c) => {
  const b = c?.before || {}, a = c?.after || {}
  if (String(b.amount ?? '') !== String(a.amount ?? '') || (b.currency ?? null) !== (a.currency ?? null)) return 'amount'
  if ((b.expires_on ?? null) !== (a.expires_on ?? null)) return 'deadline'
  return 'amount'   // the server would not have sent it; label it the consequential way
}
// A deposit deadline. Date only — the time of day is noise on a date someone set by phone.
const fmtDeadline = (s) => {
  const d = s?.expires_on
  if (!d) return '—'
  const t0 = new Date(`${String(d).slice(0, 10)}T12:00:00`)
  return Number.isNaN(t0.getTime()) ? String(d)
    : t0.toLocaleDateString(getLanguage() === 'ka' ? 'ka-GE' : 'en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
}

const SHARE_KIND_KEYS = {
  meeting_requested: 'desk_notif_share_meeting',
  opened: 'desk_notif_share_opened',
  call_clicked: 'desk_notif_share_call',
  whatsapp_clicked: 'desk_notif_share_whatsapp',
  engaged: 'desk_notif_share_engaged',
}

// ⚠️ `ჩაერთო` DOES NOT TAKE AN ERGATIVE SUBJECT. Every other row here is built on
// a transitive verb (გახსნა, მოითხოვა, დააჭირა) whose subject is ergative — but
// this one is intransitive, so its subject stays nominative: „კლიენტი ჩაერთო",
// never „კლიენტმა ჩაერთო". Running shareSubject() over it would produce exactly
// the class of error the doubled `-მა` was. Hence a second fallback string rather
// than a second suffix rule.
const NOMINATIVE_KINDS = new Set(['engaged'])

const NO_WORKED = new Set()
const NO_READ = { read: new Set(), dismissed: new Set() }

// ── The undo window lives OUTSIDE the component (B360) ──────────────────────
// Owner ruling 2026-08-07: *"five seconds should mean five seconds. Closing a panel is
// navigation, not intent."* It used to be `useState` inside the feed, so closing the bell
// unmounted the only thing holding the promise and the dismissal became permanent instantly —
// with no recourse anywhere in the app. The copy said one thing and the app did another,
// which is the class of defect this surface keeps producing.
//
// Module scope, not a context or a prop: the SAME dismissal has to survive the feed being
// unmounted and remounted, and it must carry across the two containers that render this feed
// (the desktop rail and the mobile slide-over). A value that outlives the component cannot
// live inside it.
//
// ⚠️ THE DEADLINE IS WALL-CLOCK, and the timer below is scheduled for the REMAINING time, not
// a fresh 5s on every mount. Otherwise reopening the bell would silently extend the promise —
// a different lie in the same place, and the harder one to notice.
// ONE slot, still: a second dismissal replaces the first. An undo stack in a notification
// panel is a feature nobody asked for; five seconds is the promise, not a history.
const UNDO_MS = 5000
let _pendingUndo = null            // { key, at } — survives unmount by construction
const liveUndo = () => (_pendingUndo && Date.now() - _pendingUndo.at < UNDO_MS ? _pendingUndo : null)

export function NotificationsFeed({ history = [], accessRows = [], accessPending = [], shareActivity = [], aiAlerts = [], systemAlerts = [], isAdminViewer = false, meetingNudges = null, binderChanges = null, clientWaiting = [], onOpenMeeting = null, agentId = null, worked = NO_WORKED, readState = NO_READ, onMarkRead = null, onDismiss = null, onUndoDismiss = null, onOpenActivity, onOpenListing, onOpenDemand = null, onAccessDecided = null }) {
  // The last dismissal, held for its undo window — see the module-level slot above for why it
  // is not state. This mirrors it so the row re-renders; the slot is what makes the promise
  // survive the panel closing (B360). Seeded from the slot on MOUNT, so reopening the bell
  // inside the window finds the undo still there.
  const [undoRow, setUndoRow] = useState(liveUndo)
  const clearUndo = () => { _pendingUndo = null; setUndoRow(null) }
  useEffect(() => {
    if (!undoRow) return
    // Remaining time, never a fresh 5s — a remount must not extend the promise. Already
    // expired (a stale mirror) clamps to 0 and clears on the next tick.
    const left = Math.max(0, UNDO_MS - (Date.now() - undoRow.at))
    const id = setTimeout(() => {
      if (_pendingUndo === undoRow) _pendingUndo = null
      setUndoRow(null)
    }, left)
    return () => clearTimeout(id)
  }, [undoRow])
  // Zone B's overflow, expanded in place (B357). Default collapsed on every open: the
  // cap is a statement about what a hint feed should be, so it must reassert itself
  // rather than stay expanded from a previous visit.
  const [showAllB, setShowAllB] = useState(false)
  const dismiss = (row) => {
    const k = rowKey(row)
    if (!k) return
    onDismiss?.(k)
    const entry = { key: k, at: Date.now() }
    _pendingUndo = entry
    setUndoRow(entry)
  }
  // Ambient streams fetched here on open (not at Desk mount): the feed is an interrupt
  // surface, opened deliberately — no reason to pay for price history on every Desk load.
  const [priceRows, setPriceRows] = useState([])
  // Review-queue pointers. Fetched with the rest of the ambient streams on open, and
  // fail-silent: the endpoint answers [] for an agent, so a failure and "nothing to
  // show" degrade to the same harmless empty block.
  const [queues, setQueues] = useState([])
  useEffect(() => { getReviewQueues().then(setQueues).catch(() => {}) }, [])
  // loaded gates the empty state — "Nothing yet" while the fetch is in flight reads as a
  // broken feed (owner report 2026-07-24); until it settles the feed says loading instead.
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    // Stale-response guard: switching agentId mid-flight must not let the previous
    // agent's rows (or its "loaded") land after the new request was issued.
    let cancelled = false
    setLoaded(false)
    setPriceRows([])
    getPriceChanges({ days: 7, agentId })
      .then(rows => { if (!cancelled) setPriceRows(rows) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [agentId])
  // Scheduled shares that finished (share_plans.js /notices): published, failed or missed. Fetched
  // here rather than threaded through IntakeTab — the feed is the only consumer. Every minute,
  // the same cadence the server's tick moves runs at.
  const [shareRuns, setShareRuns] = useState([])
  useEffect(() => {
    let dead = false
    const load = () => getSharePlanNotices().then(r => { if (!dead) setShareRuns(r?.notices || []) }).catch(() => {})
    load()
    const id = setInterval(load, 60_000)
    return () => { dead = true; clearInterval(id) }
  }, [])
  const notifRows = useMemo(() =>
    history.filter(h => h.status === 'pending' || h.status === 'failed' || h.status === 'rejected')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
  [history])
  const priceChangeRows = useMemo(() => priceRows.filter(r => (r.type || 'price_change') === 'price_change'), [priceRows])
  // "No longer available" / price moves are PERSONAL ambient signals — they mean
  // "a listing YOU work has changed". An admin has no agents row, so agentId is
  // null and getPriceChanges returns the COMPANY-WIDE firehose: the owner's bell
  // filled with ended listings belonging to nobody in particular (reported
  // 2026-08-05). Suppressed for the admin viewer rather than re-scoped, because
  // there is no honest personal scope for someone who owns no listings — the
  // company-wide view of the same data is the Listings/აქტივობა surface, which is
  // built for it. Agents are unaffected.
  const endedRows = useMemo(() => (isAdminViewer ? [] : priceRows.filter(r => r.type === 'listing_ended')), [priceRows, isAdminViewer])
  // One interleaved feed sorted newest-first across all sources, not stacked blocks — a
  // fresh price move should be able to outrank a stale pending draft. Cap 48 (the tile's
  // 24 was sized for a card-height strip; a full-height panel can honestly show more).
  const feedRows = useMemo(() => [
    ...notifRows.map(item => ({ kind: 'notif', item, at: item.created_at })),
    ...priceChangeRows.map((pc, i) => ({ kind: 'price', pc, key: `${pc.listing_id}-${pc.observed_at}-${i}`, at: pc.observed_at })),
    ...endedRows.map((ec, i) => ({ kind: 'ended', ec, key: `ended-${ec.listing_id}-${ec.observed_at}-${i}`, at: ec.observed_at })),
    ...accessRows.map(a => ({ kind: 'access', a, key: `access-${a.id}`, at: a.decided_at })),
    // Inbound asks (scope=against-me) — the only ACTIONABLE access row. Sorted in
    // with everything else by time rather than pinned: a colleague waiting since
    // yesterday should not outrank a request from an hour ago just by being an ask.
    ...accessPending.map(a => ({ kind: 'ask', a, key: `ask-${a.id}`, at: a.created_at })),
    ...shareActivity.map(s => ({ kind: 'share', s, key: `share-${s.id}`, at: s.at })),
    // AI health alerts (admin-only stream — empty array for agents). Raised by the
    // server only when the AI is GENUINELY broken (bad key / quota-funds out /
    // failure streak), never for routine one-off errors.
    ...aiAlerts.map(al => ({ kind: 'ai_alert', al, key: `ai-${al.finding_key}`, at: al.last_seen })),
    // Background-worker failures (admin-only stream — empty array for agents).
    // Same shape and same rule as ai_alert: not clickable, because the fix lives
    // on the box / in Admin, not behind a Desk callback. See getSystemAlerts.
    ...systemAlerts.map(sa => ({ kind: 'system_alert', sa, key: `sys-${sa.key}`, at: sa.at })),
    // Meeting follow-up nudges (0427). All three tiers ride the feed; only
    // critical+high badge (IntakeTab's stripNotifCount) — medium is ambient.
    // `at` is the moment the nudge is ABOUT: the missed appointment, or the last
    // touch that left the row stale.
    // Medium capped at 3 ROWS (designer 2026-08-06): the pile (177 agency-wide on
    // day one) is stated as ONE sentence after the feed — see the tail line below —
    // never as rows that bury the streams that badge.
    // 'upcoming' (0451) rides the same shape but carries `tier: 'upcoming'`, which is what
    // rowPrefix turns into its own key prefix. ⚠️ Its `at` is the FUTURE appointment time,
    // so within zone A it sorts above everything retrospective — which is right: the thing
    // that has not happened yet is the one you can still act on.
    ...['critical', 'high', 'medium', 'upcoming', 'gone'].flatMap(tier => (meetingNudges?.[tier] || [])
      .slice(0, tier === 'medium' ? 3 : Infinity)
      .map(n => ({ kind: 'nudge', n: { ...n, tier }, key: `nudge-${tier}-${n.id}`, at: n.scheduled_at || n.updated_at }))),
    // A deposit figure REWRITTEN after it was first recorded (owner ruling B, 2026-08-06).
    // ⚠️ NOT capped and NOT tiered, unlike the medium nudges above. The cap exists because
    // 177 missing reasons are a chore whose pile is better stated in a sentence; these are
    // individually consequential — the whole point is that someone changed a number about
    // money, and "and 4 more" would hide exactly the one that matters. If this list is ever
    // long enough to need a cap, that IS the finding and it belongs in front of the owner,
    // not behind a tail line.
    // `at` is when the CHANGE happened, which is the moment being reported.
    ...(binderChanges?.changes || []).map(c => ({ kind: 'binder_change', c, key: `bc-${c.event_id}`, at: c.at })),
    ...shareRuns.map(r => ({ kind: 'share_run', r, key: `sr-${r.id}`, at: r.updated_at })),
    ...(clientWaiting || []).map(w => ({ kind: 'client_waiting', w, key: `cw-${w.last_in_id}`, at: w.seen_at })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at)), [notifRows, priceChangeRows, endedRows, accessRows, accessPending, shareActivity, aiAlerts, systemAlerts, meetingNudges, binderChanges, shareRuns, clientWaiting])

  // Partition, THEN render. The sort above still runs, but only ever inside a
  // zone — see the classify() header for why the zones are the budget.
  //
  // Dismissed rows leave the feed here, before the zones exist. Only class B can
  // ever be in that set — the server refuses to write 'dismissed' for a class-A
  // key — so this filter cannot hide something owed to a person even if a stale
  // key survived a reclassification.
  //
  // ⚠️ B359 — THE FILTER IS NOT CONDITIONED ON THE CLASS, and that is the fix, not an
  // oversight. It used to read `cls === 'B' && …`, which looked like a safety belt and was
  // actually a hole: `price`/`ended` are CONDITIONAL rows, class B only while the viewer is
  // working that flat. Dismiss one, then stop working the flat — the share lapses, the
  // cooperation closes — and it reclassifies to C, where the filter no longer applied, so the
  // row the agent had already dismissed came back inside the ambient count. "Dismissed" meant
  // "dismissed for as long as you keep caring", which is not a thing anyone can rely on.
  // A dismissal is a fact about a ROW and a VIEWER; it does not expire when the row's class
  // moves.
  //
  // ⚠️ THE GUARD IS `cls !== 'A'`, NOT `cls === 'B'` AND NOT NOTHING — the two wrong answers
  // fail in opposite directions and both have been in this file today. `=== 'B'` was the
  // original hole (B359: a dismissed price row reclassifies to C and returns). Dropping the
  // class test entirely, which is how B359 was first fixed, closes that hole but throws away
  // the belt: if a class-A key ever reaches the dismissed set — stale data, a future writer,
  // a server bug — a debt owed to a PERSON would be silently hidden, which is the one failure
  // this panel must never have. `!== 'A'` is the predicate that has both properties: a
  // dismissal survives B→C reclassification, and nothing can hide an A row.
  // The server refusing to WRITE 'dismissed' for a class-A key is still the primary defence;
  // this is defence in depth, and it costs one comparison.
  const zones = useMemo(() => {
    const A = [], B = [], C = []
    for (const r of feedRows) {
      const cls = classify(r, worked)
      if (cls !== 'A' && readState.dismissed.has(rowKey(r))) continue
      ({ A, B, C })[cls].push(r)
    }
    return { A, B, C }
  }, [feedRows, worked, readState])
  // The three streams, each stated as one line. `lifted` counts what zone B took
  // out of that same stream, so the digest can say so out loud.
  const streams = useMemo(() => ({
    price: zones.C.filter(r => r.kind === 'price'),
    ended: zones.C.filter(r => r.kind === 'ended'),
    opens: zones.C.filter(r => r.kind === 'share'),
  }), [zones.C])
  const lifted = useMemo(() => ({
    price: zones.B.filter(r => r.kind === 'price').length,
    ended: zones.B.filter(r => r.kind === 'ended').length,
    opens: zones.B.filter(r => r.kind === 'share').length,
  }), [zones.B])
  // How many medium nudges are NOT on screen and NOT already dismissed — see the tail line's
  // comment. The visible ones are whatever survived `zones`; the pile is the server's list
  // minus everything the agent has dismissed, whether it was ever rendered or not.
  const mediumTail = useMemo(() => {
    const all = meetingNudges?.medium || []
    if (!all.length) return 0
    const shownIds = new Set([...zones.A, ...zones.B, ...zones.C]
      .filter(r => r.kind === 'nudge' && r.n.tier === 'medium').map(r => r.n.id))
    // ⚠️ Built through rowKey, never hand-spelled: the key carries the `why` suffix as well
    // as the tier prefix, and a near-miss here would silently count every row as un-dismissed
    // — the exact bug this memo exists to fix, one layer down.
    const live = all.filter(n => !readState.dismissed.has(rowKey({ kind: 'nudge', n: { ...n, tier: 'medium' } })))
    return Math.max(0, live.length - shownIds.size)
  }, [meetingNudges, zones, readState])

  const renderRow = row => row.kind === 'ai_alert' ? (
            // Not clickable — the fix lives in Admin → AI, outside the Desk's callbacks.
            // detail = stable human label ("quota exhausted…"); sample = last HTTP/error.
            <div key={row.key} className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30">
              <span className="shrink-0 text-sm leading-5 text-red-500"><Icon name="alert-triangle" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-red-600 dark:text-red-400 font-medium">
                  {t('desk_notif_ai_alert')}
                  <span className="text-muted-foreground font-normal"> · {row.al.platform}</span>
                </span>
                <span className={TXT_VALUE}>{row.al.detail}</span>
                {row.al.sample && <span className={`block ${TXT_META}`}>{row.al.sample}</span>}
                <span className={`block ${TXT_META}`}>{timeAgo(row.al.last_seen)}</span>
              </span>
            </div>
          ) : row.kind === 'system_alert' ? (
            // Background-worker failure. Not clickable (same reason as ai_alert: the
            // fix is on the box). 'warn' renders amber, 'error' red — a starved portal
            // and a dead cron should not look identical.
            <div key={row.key} className={`flex items-start gap-2 px-2 py-1.5 rounded-lg ${
              row.sa.severity === 'warn'
                ? 'bg-amber-500/10 border border-amber-500/30'
                : 'bg-red-500/10 border border-red-500/30'}`}>
              <span className={`shrink-0 text-sm leading-5 ${row.sa.severity === 'warn' ? 'text-amber-500' : 'text-red-500'}`}>
                <Icon name="alert-triangle" />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-xs font-medium ${row.sa.severity === 'warn'
                  ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                  {t('desk_notif_system_alert')}
                  <span className="text-muted-foreground font-normal"> · {row.sa.worker}</span>
                </span>
                <span className={TXT_VALUE}>{row.sa.message}</span>
                <span className={`block ${TXT_META}`}>
                  {timeAgo(row.sa.at)}
                  {row.sa.repeats > 1 && ` · ×${row.sa.repeats}`}
                </span>
              </span>
            </div>
          ) : row.kind === 'binder_change' ? (
            // Someone rewrote a deposit after it was first recorded. This row is the
            // ENTIRE answer to "anyone on the meeting can change the money" — the owner
            // chose transparency over a permission wall, so the row has one job: make the
            // change impossible to miss, and say enough that it can be challenged without
            // opening anything.
            // Red, and no tier logic: there is no mild version of this. It is rare by
            // construction (a first save never appears), so it cannot become wallpaper.
            // ⚠️ NOT ALWAYS CLICKABLE. A binder may have no meeting — 0443 made that link
            // nullable on purpose ("a deposit may exist before a deal exists") — and a button
            // that opens nothing is the same defect as the deep link that asked nothing, which
            // a real agent found in this bell within minutes. The row still has to APPEAR: the
            // money moved either way. It just stops pretending there is somewhere to go.
            <button key={row.key} type="button"
              disabled={!row.c.meeting_id && !row.c.legacy_viewing_id}
              onClick={() => onOpenMeeting?.({
                // The nudge opener speaks {id, legacy_viewing_id}; give it exactly that
                // shape rather than a second lookup path that can rot on its own.
                id: row.c.meeting_id, legacy_viewing_id: row.c.legacy_viewing_id })}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent border bg-red-500/10 border-red-500/30 disabled:hover:bg-transparent disabled:cursor-default">
              <span className="shrink-0 text-sm leading-5 text-red-500"><Icon name="alert-circle" size={14} /></span>
              <span className="min-w-0 flex-1">
                {/* ⚠️ THE ROW MUST NAME WHAT ACTUALLY MOVED. Caught by rendering it: an
                    expiry-only change produced "Deposit amount changed · 3,000 USD →
                    3,000 USD", an alert that visibly contradicts itself and teaches the
                    reader that this stream is noise. The server counts a quietly extended
                    deadline as a change ON PURPOSE, so the display has to keep up with it. */}
                <span className="block text-xs font-medium">
                  {t(binderMoved(row.c) === 'deadline' ? 'desk_binder_deadline_changed' : 'desk_binder_changed')}
                </span>
                {/* FROM → TO, on its own line and never truncated: the two values are the
                    whole message. Currency is always printed, because 5,000 GEL → 5,000 USD
                    is a change the digits alone would hide. */}
                <span className="block text-xs tabular-nums">
                  {binderMoved(row.c) === 'deadline'
                    ? <>{fmtDeadline(row.c.before)} → {fmtDeadline(row.c.after)}</>
                    : <>{fmtBinder(row.c.before)} → {fmtBinder(row.c.after)}</>}
                </span>
                {/* When the AMOUNT moved and the deadline moved with it, the deadline is
                    still worth a line — but quietly, under the money. */}
                {binderMoved(row.c) === 'amount'
                  && (row.c.before?.expires_on || null) !== (row.c.after?.expires_on || null) && (
                  <span className="block text-xs text-muted-foreground tabular-nums">
                    {fmtDeadline(row.c.before)} → {fmtDeadline(row.c.after)}
                  </span>
                )}
                {/* WHY, when the writer demanded one (B351). `amend_binder` refuses to restate
                    a CONFIRMED amount without a reason, so where this line appears it is the
                    person's own words, and where it does not the amount was still unconfirmed —
                    the absence is information, which is why there is no "no reason given"
                    placeholder. Quoted and not truncated: a half-shown justification is worse
                    than none, and this is the sentence that decides whether to challenge.
                    ⚠️ The legacy `meeting.binder_set` writer cannot supply this at all, so its
                    rows never show it. Do not "fix" that by inventing a default. */}
                {row.c.reason && (
                  <span className="block text-xs italic text-muted-foreground">
                    „{row.c.reason}"
                  </span>
                )}
                {/* WHICH deal, same rule as the nudge row: address or district, never a uuid. */}
                {(row.c.address_text || row.c.district_code) && (
                  <span className="block text-xs truncate text-muted-foreground">
                    {row.c.address_text || row.c.district_code}
                    {row.c.client_name ? ` · ${row.c.client_name}` : ''}
                  </span>
                )}
                {/* WHO and WHEN. An unnamed actor prints as "someone" rather than a blank:
                    the sentence must stay true when the party lookup misses. */}
                <span className="block text-xs text-muted-foreground">
                  {row.c.by_name || t('desk_binder_changed_someone')} · {timeAgo(row.c.at)}
                </span>
              </span>
            </button>
          ) : row.kind === 'nudge' ? (
            // A meeting owing an update (0427). Clickable: the fix is 1–2 taps on the
            // meeting's own status/reason chips, so the row goes straight there.
            // Designer 2026-08-06: the GLYPH names the state (calendar = appointment
            // passed, hourglass = pending out of time, alert-circle = missing reason);
            // MEDIUM LOSES ITS FILL — a tinted amber row is how the bell says "a
            // background job is failing", and a missing reason is not that. Hairline
            // border, amber only in the glyph: present, countable, not an alert.
            // Chevron: the alert rows keep none (genuinely inert); a nudge row is a
            // door and says so without hover, which the review phone does not have.
            <button key={row.key} type="button" onClick={() => onOpenMeeting?.(row.n)}
              className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent border ${
                row.n.tier === 'critical' ? 'bg-red-500/10 border-red-500/30'
                : row.n.tier === 'high' ? 'bg-orange-500/10 border-orange-500/30'
                : 'border-border'}`}>
              {/* Upcoming is SKY, not amber. Amber and red on this surface mean "something
                  is broken"; a viewing that has not happened yet is not that — it is the
                  one row here reporting something still going right. */}
              <span className={`shrink-0 text-sm leading-5 ${
                row.n.tier === 'critical' ? 'text-red-500' : row.n.tier === 'high' ? 'text-orange-500'
                : row.n.tier === 'upcoming' ? 'text-sky-500' : row.n.tier === 'gone' ? 'text-violet-500' : 'text-amber-500'}`}>
                {/* ⚠️ THE SHARED MAP, not a fourth private copy (fixed 2026-08-15).
                    NUDGE_WHY_ICON exists and says in its own comment that it is "shared with
                    the bell" — and grep found exactly one reference to it: its declaration.
                    The inline map here was missing `binder_expired` and `missing_next_action`,
                    so both fell through to 'calendar': an EXPIRED BINDER (money on a clock
                    that ran out) drew the glyph reserved for "an appointment passed", while
                    the same nudge drew an alert-triangle on every other surface. The rule the
                    map exists to enforce is that the glyph names the STATE, not the object. */}
                <Icon name={NUDGE_WHY_ICON[row.n.why] || 'calendar'} size={14} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-medium">{tOr(`desk_nudge_why_${row.n.why}`, row.n.why)}</span>
                {/* WHICH meeting (owner-caught 2026-08-06): the subject line — address
                    or district, viewer-entitled by the endpoint's own scoping. */}
                {(row.n.property?.address || row.n.property?.district_code) && (
                  <span className="block text-xs truncate">{row.n.property.address || row.n.property.district_code}</span>
                )}
                <span className={`block ${TXT_META}`}>
                  {/* WHOSE meeting — present only on someone ELSE's row, which in practice
                      means a manager looking at their offices. A team row that cannot be
                      attributed cannot be chased: without this, chasing means opening every
                      row to find out who to ask.
                      It leads the meta line rather than joining the subject line above,
                      because at 380px the address already competes for that line — and the
                      name is what a manager scans for, so it must not be the thing that
                      truncates. */}
                  {row.n.agent_name && <span className="font-medium text-foreground">{row.n.agent_name} · </span>}
                  {/* Through the registry, not the raw key: 'rescheduled' as a STATE has
                      its own string — the raw desk_meeting_status_ key is the ACTION
                      chip ("გადადება…") and would misname the row. */}
                  {t(MEETING_STATUS_KEYS[row.n.status] || `desk_meeting_status_${row.n.status}`)}
                  {row.n.waiting_on ? ` · ${tOr(`desk_nudge_waiting_${row.n.waiting_on}`, row.n.waiting_on)}` : ''}
                  {/* fmtDay, not the raw column: this printed "2026-08-20" beside a
                      localized elapsed form on the same line. Same fix the feed took. */}
                  {row.n.next_action_date ? ` · ${fmtDay(row.n.next_action_date)}` : ''}
                  {/* ⚠️ timeAgo() IS PAST-ONLY. Its first branch is `s < 60 → 'just now'`,
                      and a future timestamp makes s negative — so a viewing scheduled for
                      tomorrow rendered "just now", which is the opposite of the fact. An
                      upcoming row therefore states the APPOINTMENT TIME; every other tier
                      here is retrospective and keeps the elapsed form. */}
                  {/* TBILISI, never the device (owner ruling 2026-08-12). This is the only
                      line telling the agent when an imminent meeting starts, and on a device
                      in another zone it named the wrong hour — and across midnight the wrong
                      weekday. `timeAgo` is elapsed math and is zone-independent. */}
                  {row.n.scheduled_at
                    ? ` · ${row.n.tier === 'upcoming'
                        ? fmtTbilisi(row.n.scheduled_at, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
                        : timeAgo(row.n.scheduled_at)}`
                    : ''}
                </span>
              </span>
              <span className="shrink-0 self-center text-muted-foreground">›</span>
            </button>
          ) : row.kind === 'ask' ? (
            <AccessAskRow key={row.key} r={row.a} onDecided={onAccessDecided} />
          ) : row.kind === 'client_waiting' ? (
            <ClientWaitingRow key={row.key} w={row.w} />
          ) : row.kind === 'share_run' ? (
            // A scheduled share finished (share_plans.js). Opens the listing; a published one
            // also links the new ad. Failed / missed carry the reason the PC gave or waited on.
            <button key={row.key} type="button"
              onClick={() => onOpenListing?.({ listing_id: row.r.share_plan?.listing_id })}
              className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent ${row.r.state === 'published' ? '' : 'bg-destructive/5'}`}>
              <span className="shrink-0 text-sm leading-5">{row.r.state === 'published' ? '✓' : row.r.state === 'missed' ? '⏰' : '✗'}</span>
              <span className="min-w-0 flex-1 text-xs">
                <span className="font-medium">{t(`notif_share_run_${row.r.state}`)}</span>
                {' · '}{({ myhome: 'MyHome', ss: 'SS.ge', korter: 'Korter' })[row.r.platform] || row.r.platform}
                <span className="block text-muted-foreground truncate">
                  {[row.r.share_plan?.street, row.r.share_plan?.number].filter(Boolean).join(' ') || row.r.share_plan?.address || ''}
                </span>
                {row.r.state !== 'published' && row.r.message && <span className="block text-destructive">{row.r.message}</span>}
                {row.r.state === 'published' && row.r.link && (
                  <a href={row.r.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                    className="text-emerald-600 underline">{t('desk_share_job_open')}</a>
                )}
              </span>
              <span className="shrink-0 self-center text-muted-foreground">›</span>
            </button>
          ) : row.kind === 'share' ? (
            // Clickable when the server could name a listing (2026-08-02): "a client
            // asked to view" is useless if the agent can't tell WHICH flat, and the
            // district alone doesn't say. Opens the same lineage card every other
            // Desk surface opens.
            //
            // 2026-08-04: a row with no active listing is no longer a dead div. It
            // opens the PROPERTY workspace instead — the honest destination for "the
            // client is holding a link to something that is off-market", and the same
            // fallback the pasted-link resolver uses. Inert only when the server could
            // name neither, which should not happen.
            (() => {
              const dest = row.s.listing_id || row.s.property_id
              const Row = dest ? 'button' : 'div'
              return (
                <Row key={row.key}
                  {...(dest ? {
                    type: 'button',
                    onClick: () => onOpenListing?.({
                      listing_id: row.s.listing_id, property_id: row.s.property_id,
                      is_own_listing: row.s.is_own_listing,
                    }),
                  } : {})}
                  className={`w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg transition-colors ${
                    row.s.kind === 'meeting_requested' ? 'bg-emerald-500/10 border border-emerald-500/30' : ''} ${
                    dest ? 'hover:bg-accent' : ''}`}>
                  <span className="shrink-0 text-sm leading-5">
                    {/* The eye belongs to the engaged band alone — an `opened`
                        event has no standalone row form at all (it folds into the
                        stream), so there is nothing for it to be confused with. */}
                    <Icon name={row.s.kind === 'meeting_requested' ? 'calendar'
                      : row.s.kind === 'engaged' ? 'eye' : 'link'} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-xs ${row.s.kind === 'meeting_requested' ? 'text-emerald-600 dark:text-emerald-400 font-medium' : ''}`}>
                      {tLog(
                        row.s.kind === 'engaged' && row.s.also_gallery
                          ? 'desk_notif_share_engaged_gallery'
                          : (SHARE_KIND_KEYS[row.s.kind] || 'desk_notif_share_opened'),
                        { name: NOMINATIVE_KINDS.has(row.s.kind)
                          ? (row.s.recipient_name || t('desk_notif_share_someone_nom'))
                          : shareSubject(row.s.recipient_name) })}
                      {rowDistrictLabel(row.s) && <span className="text-muted-foreground font-normal"> · {rowDistrictLabel(row.s)}</span>}
                    </span>
                    {/* WHICH FLAT, and what the agent quoted for it — the row used to
                        say "a client opened a property you shared · საბურთალო", which
                        names no flat at all. The address ships only on this feed, and
                        only because it is scoped to the SENDER (see the reasoning in
                        core_share.js's share-activity handler); if that scoping is ever
                        widened, this line must be re-gated with it.
                        The price is the FROZEN quote to this client (0196), never the
                        listing's current price — otherwise the row would disagree with
                        the link the client is holding. */}
                    {(row.s.address_text || row.s.offered_price != null) && (
                      <span className="block text-xs truncate">
                        {row.s.address_text}
                        {row.s.address_text && row.s.offered_price != null && ' · '}
                        {row.s.offered_price != null && (
                          <span className="font-medium">
                            {/* display/rate deliberately null → moneyText returns the
                                amount in ITS OWN currency with no conversion. This is a
                                quote frozen at mint, not a market number: showing an
                                agent "≈1,890 USD" for the 5,000 GEL they wrote to a
                                client would misstate what was actually promised. */}
                            {moneyText(row.s.offered_price, row.s.offered_currency, null, null)}
                          </span>
                        )}
                      </span>
                    )}
                    <span className={`block ${TXT_META}`}>{timeAgo(row.s.at)}</span>
                  </span>
                </Row>
              )
            })()
          ) : row.kind === 'notif' ? (
            <button key={row.item.id} onClick={() => onOpenActivity?.(row.item)}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
              <span className="shrink-0 text-sm leading-5">{row.item.status === 'failed' || row.item.status === 'rejected' ? <Icon name="alert-triangle" /> : activityLabel(row.item).icon}</span>
              <span className="min-w-0 flex-1">
                <span className={TXT_VALUE}>
                  {row.item.status === 'failed' || row.item.status === 'rejected' ? t('desk_notif_failed') : tLog('desk_notif_review_row', { text: activityLabel(row.item).text })}
                  {activityLabel(row.item).obj ? <span className="text-muted-foreground"> · {activityLabel(row.item).obj}</span> : null}
                </span>
                <span className={`block ${TXT_META}`}>{timeAgo(row.item.created_at)}</span>
              </span>
            </button>
          ) : row.kind === 'price' ? (
            <button key={row.key} onClick={() => onOpenListing?.(row.pc)}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
              <span className="shrink-0 text-sm leading-5">{row.pc.direction === 'down' ? <Icon name="trending-down" /> : <Icon name="trending-up" />}</span>
              <span className="min-w-0 flex-1">
                <span className={TXT_VALUE}>
                  <span className={row.pc.direction === 'down' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                    {row.pc.from ?? '—'} {row.pc.currency || ''} → {row.pc.to ?? '—'} {row.pc.currency || ''}
                    {row.pc.pct != null && <span className="ml-1 tabular-nums">({row.pc.pct > 0 ? '+' : ''}{row.pc.pct}%)</span>}
                  </span>
                  {(row.pc.address || row.pc.district) && <span className="text-muted-foreground"> · {row.pc.address || row.pc.district}</span>}
                </span>
                <span className={`block ${TXT_META}`}>{row.pc.source} · {timeAgo(row.pc.observed_at)}</span>
              </span>
            </button>
          ) : row.kind === 'access' ? (
            // 0158 step 4: "your access request was decided". Requirement rows open
            // the demand (granted ⇒ its contact pill now renders); property rows have
            // no card to open from here — the panel on the listing shows the reveal.
            <button key={row.key}
              onClick={() => row.a.requirement_id && onOpenDemand?.(row.a.requirement_id)}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
              <span className="shrink-0 text-sm leading-5">
                <Icon name={row.a.status === 'granted' ? 'check-circle' : row.a.status === 'revoked' ? 'lock' : 'ban'} />
              </span>
              <span className="min-w-0 flex-1">
                <span className={TXT_VALUE}>
                  <span className={row.a.status === 'granted' ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-amber-600 dark:text-amber-400'}>
                    {t(row.a.status === 'granted' ? 'desk_notif_access_granted'
                      : row.a.status === 'revoked' ? 'desk_notif_access_revoked' : 'desk_notif_access_denied')}
                  </span>
                  {/* Never district_code: districtLabel has no code fallback by rule, so
                      when ancestry is missing this says "a property" rather than leaking
                      an internal identifier. No location beats a token on screen. */}
                  <span className="text-muted-foreground"> · {row.a.requirement_id
                    ? (row.a.requirement?.title || t('desk_notif_access_req_generic'))
                    : (rowDistrictLabel(row.a) || t('desk_notif_access_prop_generic'))}</span>
                  {row.a.decision_note && <span className="text-muted-foreground"> — {row.a.decision_note}</span>}
                </span>
                <span className={`block ${TXT_META}`}>{timeAgo(row.a.decided_at)}</span>
              </span>
            </button>
          ) : (
            <button key={row.key} onClick={() => onOpenListing?.(row.ec)}
              className="w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
              <span className="shrink-0 text-sm leading-5"><Icon name="ban" /></span>
              <span className="min-w-0 flex-1">
                <span className={TXT_VALUE}>
                  No longer found on {row.ec.source || 'the platform'}
                  {(row.ec.address || row.ec.district) && <span className="text-muted-foreground"> · {row.ec.address || row.ec.district}</span>}
                </span>
                <span className={`block ${TXT_META}`}>{row.ec.source} · {timeAgo(row.ec.observed_at)}</span>
              </span>
            </button>
          )

  // Loading gates the whole panel — "nobody is waiting" while the fetch is in
  // flight is a claim we cannot make yet, and it is the one line an agent is
  // most likely to believe.
  if (!loaded && !feedRows.length) {
    return <p className={`px-4 py-3 ${TXT_META}`}>{t('desk_notif_panel_loading')}</p>
  }
  const allEmpty = !zones.A.length && !zones.B.length && !zones.C.length

  return (
    <div className="flex flex-col min-h-0">
      {/* Zones A and B scroll; the streams block sits outside it (see the panel
          container). On a short day the whole thing is shorter than the viewport
          and nothing is pinned to anything — the dropdown is content-height.
          ⚠️ NOT `flex-1`. The panel is `h-auto` + `max-h`, so its column has no
          definite height to divide and a `flex-1` child collapses — measured at
          2px in the render harness, with the whole feed present but invisible.
          `min-h-0` + `overflow-y-auto` grows to content and scrolls only once the
          parent's max-height actually binds, which is the behaviour the pinned
          streams block depends on. `vite build` cannot see this. */}
      <div className="min-h-0 overflow-y-auto p-2 space-y-0.5">
        {/* ── A · waiting on you ──────────────────────────────────────────────
            The ONLY zone that renders when empty. Its empty line is the
            definition of the surface (designer round 1): the emptiest state is
            where an agent learns what this panel is for, and measured p50 for A
            is ZERO, so this is the line most agents see most mornings. It is a
            statement, never a count of 0. */}
        <ZoneLabel label={t('desk_notif_zone_waiting')} n={zones.A.length} />
        {zones.A.length === 0 ? (
          <div className="px-2 py-1.5">
            <p className={TXT_VALUE}>{t('desk_notif_zone_empty_a')}</p>
            {allEmpty && <p className={`${TXT_META} mt-1`}>{t('desk_notif_empty_hint')}</p>}
          </div>
        ) : zones.A.map(row => {
          const k = rowKey(row)
          const unread = k && !readState.read.has(k)
          // 🚨 THE ALERT ROWS HAD NO WAY TO BE CLEARED, AND THE DATA PROVES NOBODY EVER DID
          // (owner report 2026-08-26: *"I have opened this notifications yesterday but they
          // still show active"*). `ai_alert` and `system_alert` are SIGNAL-ONLY — per
          // stripNotifCount an explicit per-row read is their ONLY exit, because nothing
          // downstream ever reports a broken cron or an exhausted quota as handled. But both
          // render as deliberately non-navigable panels (see renderRow: *"Not clickable — the
          // fix lives in Admin → AI"*) with no chevron, no hover and no cursor, so the
          // wrapper's onClickCapture below was the sole clearing mechanism and NOTHING ON
          // SCREEN SAID SO.
          //
          // Measured in core `notification_read`: 29 rows, 8 agents, 20 days, and **zero**
          // carrying an `alert:` prefix — while `nudge:`, `upcoming:`, `meeting:` and `ask:`,
          // every one of which has a visible click target, were marked read routinely. So
          // this was never agents ignoring the row; it was a control that did not exist.
          //
          // ⚠️ The fix is an explicit ✓, NOT making the panel look clickable. A red error
          // block that navigates nowhere must not acquire link affordance, and the
          // 2026-08-07 ruling is that clearing costs *a deliberate act rather than a glance*
          // — a button is that act. Opening the bell still clears nothing.
          //
          // ⚠️ Scoped to these two kinds ON EVIDENCE, not by taste. Every other zone-A row
          // either carries its own resolution (a nudge, an access ask, a draft — where read
          // changes nothing about the badge, so a ✓ would be a lie about what it does) or
          // already has a reachable click target. Do not widen it without re-measuring
          // `notification_read` for a prefix that is provably never written.
          const signalOnly = row.kind === 'ai_alert' || row.kind === 'system_alert'
          return (
            // Touching the row marks it read — NOT dismissed. For the rows that
            // carry their own resolution (an access ask, a nudge, a failed draft)
            // read changes nothing about the badge; for the signal-only ones
            // (a client asking to view, a broken job) it is the only resolution
            // available, and it costs a deliberate act rather than a glance.
            <div key={k || row.key} className="relative"
              onClickCapture={() => k && unread && onMarkRead?.(k)}>
              {/* Unread mark. A dot, not a tint: amber and red mean "something is
                  broken" on this surface and nothing here displaces that. */}
              {unread && <span className="absolute left-0 top-3 w-1.5 h-1.5 rounded-full bg-sky-500" />}
              {signalOnly ? (
                // Always visible, same rule as zone B's ✕: the review phone has no hover,
                // and a control an agent cannot discover is not a control.
                <div className={`flex items-start gap-1 ${unread ? 'pl-2.5' : 'pl-2.5 opacity-70'}`}>
                  <div className="min-w-0 flex-1">{renderRow(row)}</div>
                  {unread && (
                    <button type="button" onClick={() => k && onMarkRead?.(k)}
                      aria-label={t('desk_notif_mark_read')} title={t('desk_notif_mark_read')}
                      className="shrink-0 self-center px-1.5 py-1 text-[12px] text-muted-foreground hover:text-foreground">
                      ✓
                    </button>
                  )}
                </div>
              ) : (
                <div className={unread ? 'pl-2.5' : 'pl-2.5 opacity-70'}>{renderRow(row)}</div>
              )}
            </div>
          )
        })}

        {/* ── B · on your work ─────────────────────────────────────────────── */}
        {/* Capped at 5 (designer round 1). B is a hint, not a queue — and a hint
            that arrives 20 at a time has become a backlog, which is the thing
            this round removes. Sized against the 3-minute engagement threshold:
            p95 is 2.3/agent/day and the observed maximum in 30 days is 5, so the
            cap should effectively never bind. If it starts binding daily, the
            threshold moved — re-cut the cap, do not raise it quietly. */}
        {/* The undo, five seconds, one slot. A dismissed hint is gone from the only surface
            that shows it, so the promise has to be visible.
            ⚠️ IT SITS OUTSIDE THE ZONE-B BLOCK, and that is not tidying. It used to be inside,
            so dismissing the LAST class-B row emptied the zone and unmounted the undo along
            with it — the control disappeared in exactly the case where it is most needed, and
            "5 seconds to undo" became "5 seconds, unless that was your only hint". Found by
            RENDERING (harness N8): every build passed, because a control that is not there
            throws nothing. Same family as B360 itself — the copy promised what the layout
            then withdrew. */}
        {undoRow && (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-accent">
            <span className={`flex-1 ${TXT_META}`}>{t('desk_notif_dismissed')}</span>
            <button type="button" className={`${TXT_VALUE} font-medium underline`}
              onClick={() => { onUndoDismiss?.(undoRow.key); clearUndo() }}>
              {t('desk_notif_undo')}
            </button>
          </div>
        )}
        {zones.B.length > 0 && (
          <>
            <ZoneLabel label={t('desk_notif_zone_yourwork')} n={zones.B.length} />
            {(showAllB ? zones.B : zones.B.slice(0, 5)).map(row => (
              // ✕ is ALWAYS visible, never hover-revealed: the review phone has no
              // hover, and a control an agent cannot discover is not a control.
              <div key={rowKey(row) || row.key} className="flex items-start gap-1">
                <div className="min-w-0 flex-1">{renderRow(row)}</div>
                <button type="button" onClick={() => dismiss(row)}
                  aria-label={t('desk_notif_dismiss')} title={t('desk_notif_dismiss')}
                  className="shrink-0 self-center px-1.5 py-1 text-[12px] text-muted-foreground hover:text-foreground">
                  ✕
                </button>
              </div>
            ))}
            {/* B357: the overflow line was inert TEXT, so rows 6+ were unreachable —
                dismissed-by-layout rather than by the agent. It is a button now, and it
                opens IN PLACE (the StreamDigest idiom: "no chevron, it is not a door"),
                because the rows it reveals carry a ✕ each and a door would take the agent
                away from the surface those controls live on.
                ⚠️ The CAP IS UNCHANGED and the default is still 5 — the designer's sizing
                argument stands, and this is an escape hatch, not a raise. If the line
                starts being pressed daily, that is the threshold moving and it wants
                re-cutting rather than a bigger number. */}
            {zones.B.length > 5 && (
              <button type="button" onClick={() => setShowAllB(v => !v)}
                className={`w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent transition-colors ${TXT_META}`}>
                {showAllB ? t('desk_notif_less_salient') : tLog('desk_notif_more_salient', { n: zones.B.length - 5 })}
              </button>
            )}
          </>
        )}

        {/* The medium tail is NOT a row (designer 2026-08-06): one sentence, no
            border, no icon, no target — the pile named and stated as something
            that drains rather than something to work through. Same words as the
            banner's medium line, so the two surfaces agree. */}
        {/* ⚠️ THE TAIL MUST DRAIN AS THE WORK IS DONE (fixed 2026-08-15). This read the raw
            server array — `medium.length - 3` — while medium rows are class B and therefore
            DISMISSIBLE, and dismissed rows are filtered out of the feed above. So an agent
            who cleared two of the three visible rows still read "and N-3 more", a pile that
            never shrank no matter how much of it they worked. That is precisely the failure
            this tier's own design note says it must not have: a count is only honest if it
            states something that drains. Counted from what SURVIVED dismissal, minus what is
            actually on screen. */}
        {mediumTail > 0 && (
          <p className={`px-2 py-1.5 ${TXT_META}`}>
            {tLog('desk_nudge_more_medium', { n: mediumTail })}
          </p>
        )}
      </div>

      {/* ── Review queues — a POINTER, never the work ────────────────────────
          Management only (the endpoint returns [] for an agent, who cannot work
          these anyway). It sits in the floor block because a backlog is a STATE:
          2,029 person-merge candidates did not happen at a moment, so this is a
          count and never a row — the same reason zone C is counts.

          ⚠️ THE RATE IS THE FINDING, NOT DECORATION. Size alone says "big", which
          everyone knows and nobody acts on; size against rate says STALLED. Proven
          while building it: the complex-link queue read 1,377 waiting in the
          morning and 0 by the afternoon with 1,284 decided — size alone would have
          reported a backlog that no longer existed, and hidden the two that are
          genuinely stuck. A queue the server reports as empty never renders. */}
      {queues.length > 0 && (
        <div className="shrink-0 border-t border-border p-2 space-y-0.5">
          <ZoneLabel label={t('desk_notif_queues')} n={0} />
          {queues.map(q => (
            <div key={q.key} className="flex items-baseline gap-2 px-2 py-1">
              <span className={`min-w-0 flex-1 truncate ${TXT_VALUE}`}>{tOr(`desk_queue_${q.key}`, q.key)}</span>
              <span className={`shrink-0 ${TXT_META} tabular-nums`}>
                {/* Grouped: a backlog is read as a MAGNITUDE, and "2029" reads as an id.
                    en-US grouping deliberately, matching the money and area figures
                    elsewhere on this surface rather than inventing a second convention. */}
                {tLog('desk_notif_queue_rate', {
                  n: q.waiting.toLocaleString('en-US'),
                  done: q.done_month.toLocaleString('en-US'),
                })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── C · streams ──────────────────────────────────────────────────────
          Outside the scroll container. B and C do NOT render at zero: a counter
          reading 0 is noise and a heading over nothing is worse. */}
      {(streams.price.length || streams.ended.length || streams.opens.length) > 0 && (
        <div className="shrink-0 border-t border-border p-2 space-y-0.5">
          <ZoneLabel label={tLog('desk_notif_zone_streams', { days: 7 })}
            n={streams.price.length + streams.ended.length + streams.opens.length} />
          <StreamDigest icon="trending-up" label={t('desk_notif_stream_price')}
            rows={streams.price} lifted={lifted.price}
            /* by MAGNITUDE, not recency — the question is "is anything moving?" */
            sort={(a, b) => Math.abs(b.pc.pct ?? 0) - Math.abs(a.pc.pct ?? 0)}>
            {(sample, total) => (<>
              {sample.map(r => (
                <button key={r.key} type="button" onClick={() => onOpenListing?.(r.pc)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-accent">
                  <span className={`min-w-0 flex-1 truncate ${TXT_META}`}>{r.pc.address || r.pc.district || '—'}</span>
                  {r.pc.pct != null && (
                    <span className={`shrink-0 text-[12px] tabular-nums ${r.pc.direction === 'down'
                      ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {r.pc.pct > 0 ? '+' : ''}{r.pc.pct}%
                    </span>
                  )}
                  <span className="shrink-0 text-[12px] font-medium tabular-nums">
                    {moneyText(r.pc.to, r.pc.currency, null, null)}
                  </span>
                </button>
              ))}
              {total > sample.length && (
                <p className={`px-2 py-1 ${TXT_META}`}>{tLog('desk_notif_stream_rest', { n: total - sample.length })}</p>
              )}
            </>)}
          </StreamDigest>
          <StreamDigest icon="ban" label={t('desk_notif_stream_ended')}
            rows={streams.ended} lifted={lifted.ended}>
            {(sample, total) => (<>
              {sample.map(r => (
                <button key={r.key} type="button" onClick={() => onOpenListing?.(r.ec)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-accent">
                  <span className={`min-w-0 flex-1 truncate ${TXT_META}`}>{r.ec.address || r.ec.district || '—'}</span>
                  <span className={`shrink-0 ${TXT_META}`}>{r.ec.source}</span>
                </button>
              ))}
              {total > sample.length && (
                <p className={`px-2 py-1 ${TXT_META}`}>{tLog('desk_notif_stream_rest', { n: total - sample.length })}</p>
              )}
            </>)}
          </StreamDigest>
          <StreamDigest icon="link" label={t('desk_notif_stream_opens')}
            rows={streams.opens} lifted={lifted.opens}>
            {(sample, total) => (<>
              {sample.map(r => (
                <button key={r.key} type="button"
                  onClick={() => (r.s.listing_id || r.s.property_id) && onOpenListing?.({
                    listing_id: r.s.listing_id, property_id: r.s.property_id, is_own_listing: r.s.is_own_listing })}
                  className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-accent">
                  <span className={`min-w-0 flex-1 truncate ${TXT_META}`}>
                    {r.s.address_text || rowDistrictLabel(r.s) || '—'}
                  </span>
                  <span className={`shrink-0 ${TXT_META}`}>{timeAgo(r.s.at)}</span>
                </button>
              ))}
              {total > sample.length && (
                <p className={`px-2 py-1 ${TXT_META}`}>{tLog('desk_notif_stream_rest', { n: total - sample.length })}</p>
              )}
            </>)}
          </StreamDigest>
        </div>
      )}
    </div>
  )
}

// Zone heading. A count, never a badge — and never rendered for a zone that is
// empty except A, whose empty form is a sentence rather than a zero.
function ZoneLabel({ label, n }) {
  return (
    <div className="flex items-baseline gap-2 px-2 pt-2 pb-1">
      <span className={TXT_ZONE}>{label}</span>
      <span className="flex-1 h-px bg-border" />
      {n > 0 && <span className={`${TXT_META} tabular-nums`}>{n}</span>}
    </div>
  )
}

// Container: eBay model (2026-07-24 third ruling) — the app-header bell is the sole
// entry on every size; on ≥sm this renders as a dropdown card anchored under the header's
// right side (like eBay's bell menu), on mobile as a full-width sheet.
export function NotificationsPanel({ history = [], accessRows = [], accessPending = [], shareActivity = [], aiAlerts = [], systemAlerts = [], isAdminViewer = false, meetingNudges = null, binderChanges = null, clientWaiting = [], onOpenMeeting = null, agentId = null, worked = NO_WORKED, readState = NO_READ, onMarkRead = null, onDismiss = null, onUndoDismiss = null, onOpenActivity, onOpenListing, onOpenDemand = null, onAccessDecided = null, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-[70]" onClick={onClose}>
      {/* Transparent backdrop — outside-click dismisses without dimming the workbench. */}
      {/* ⚠️ B366 — CONTENT HEIGHT ON BOTH SIZES, capped. Owner ruling 2026-08-07: mobile should
          match the desktop behaviour. This was `h-full` on mobile, so a quiet day produced a
          full-screen sheet holding two lines — and the floor block's whole justification is
          that it costs nothing when there is nothing in it, which was true on the desktop card
          and false on the phone. Empty whitespace states nothing except that a sheet was
          available. The cap is slightly taller than the desktop's 75vh because a phone has less
          of it to spare, and the feed owns its own scroll either way. */}
      <div className="absolute right-0 top-0 w-full max-h-[85vh] rounded-b-xl border-b sm:max-h-[75vh] sm:top-14 sm:right-3 sm:w-[380px] sm:rounded-xl sm:border bg-background border-l border-border shadow-2xl flex flex-col desk-mode-enter"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border shrink-0">
          <Icon name="bell" size={15} />
          <span className="flex-1 text-sm font-semibold">{t('desk_strip_notifications')}</span>
          <button className="px-1 text-lg leading-none text-muted-foreground hover:text-foreground"
            aria-label={t('desk_search_collapse_tools_tooltip')} onClick={onClose}>✕</button>
        </div>
        {/* The feed owns its own scroll now (zones A+B scroll, the streams block
            does not), so this wrapper must NOT also scroll — two nested scroll
            containers is how the floor block ends up unreachable. `min-h-0`
            without `flex-1`: see the collapse warning inside NotificationsFeed. */}
        <div className="min-h-0 flex flex-col">
          <NotificationsFeed history={history} accessRows={accessRows} accessPending={accessPending} shareActivity={shareActivity} aiAlerts={aiAlerts}
            systemAlerts={systemAlerts} isAdminViewer={isAdminViewer}
            meetingNudges={meetingNudges} onOpenMeeting={onOpenMeeting} agentId={agentId} worked={worked}
            readState={readState} onMarkRead={onMarkRead} onDismiss={onDismiss} onUndoDismiss={onUndoDismiss}
            binderChanges={binderChanges} clientWaiting={clientWaiting}
            onOpenActivity={onOpenActivity} onOpenListing={onOpenListing} onOpenDemand={onOpenDemand}
            onAccessDecided={onAccessDecided} />
        </div>
      </div>
    </div>
  )
}
